
import React, { useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import MarkdownHelper from '../../utils/MarkdownHelper'
import { MarkdownWithSuperscript, SuperscriptProvider } from '../../utils/SuperscriptHelper'

/**
 * One step in the process container.
 * @typedef {{ icon: string, headline: string, body: string, ctaLink: string, ctaType: "button"|"link" }} ProcessStep
 */

export const ProcessSteps = (
	/** @type {{
     *		steps: ProcessStep[],
     *      isMobile: boolean,
     *      behavior: "scroll" | "stack"
     *  }}
     */
	props
) => {
    const [activeStep, setActiveStep] = React.useState(0)
    const [observer, setObserver] = React.useState(null)
    const containerRef = React.useRef(null)

    const [dragStartX, setDragStartX] = React.useState(0)
    const [_isDragging, setIsDragging] = React.useState(false)
    const isDragging = React.useMemo(() => _isDragging, [_isDragging])

    const handleDragStart = useCallback((e) => {
        if (!isDragging && e.target === containerRef.current) {
            e.preventDefault()
            e.stopPropagation()
            setIsDragging(true)
            setDragStartX(containerRef.current.scrollLeft + e.clientX)
        }
    }, [isDragging, dragStartX])

    const handleDragEnd = useCallback((e) => {
        setIsDragging(false)
    }, [isDragging])

    const handleDrag = useCallback((e) => {
        if (!isDragging) {
            return
        }
        e.preventDefault()
        e.stopPropagation()

        const moveBy = dragStartX - e.clientX
        containerRef.current.scrollLeft = moveBy

    }, [isDragging, dragStartX])

    React.useEffect(() => {
        window.addEventListener('mousedown', handleDragStart)
        window.addEventListener('mouseup', handleDragEnd)
        window.addEventListener('mousemove', handleDrag)

        return () => {
            window.removeEventListener('mousedown', handleDragStart)
            window.removeEventListener('mouseup', handleDragEnd)
            window.removeEventListener('mousemove', handleDrag)
        }
    }, [handleDragStart, handleDragEnd, handleDrag])

    React.useEffect(() => {
        if (props.isMobile && props.observer) {
            if (!props.observer) {
                return
            }
            props.observer.observe(containerRef.current)
        }

        return () => {
            if (!props.observer) {
                return
            }
            props.observer.unobserve(containerRef.current)
        }
    }, [props.isMobile, props.observer])

    React.useEffect(() => {
        if (props.isMobile && containerRef.current) {
            setObserver(new IntersectionObserver((e) => {
                const active = e.filter(e => e.intersectionRatio > 0)[0]
                if (!active) {
                    return
                }
                setActiveStep(Math.max(0, active.target.getAttribute('data-index')))
            }, {
                rootMargin: '5px',
                threshold: 1,
                root: containerRef.current
            }))

            return () => {
                if (!observer) {
                    return
                }
                observer.disconnect()
                setObserver(null)
            }
        }
    }, [props.isMobile, containerRef.current])
    

	return (
        <React.Fragment>
            <ol
                ref={containerRef}
                className={[
                    "process-steps",
                    props.isMobile ? "mobile" : "",
                    props.behavior,
                ].join(' ')}
            >
                {props.steps.map((step, i, arr) => 
                    <ProcessStep
                        index={i}  
                        observer={observer}
                        step={step}
                        key={i}
                        isMobile={props.isMobile}
                        showSeparator={props.behavior === 'stack'}
                        isLast={i === arr.length - 1}
                    />
                )}
            </ol>
            {props.behavior === 'scroll' && (
                <ScrollIndicator count={props.steps.length} activeStep={activeStep} />
            )}
        </React.Fragment>
	)
}

export const ScrollIndicator = (
    /** @type {{ count: number, activeStep: number }} */
    props
) => {
    return (
        <div className="scroll-indicator">
            {Array(props.count).fill(0).map((_, i) => <div key={i} className={["dot", i === props.activeStep ? "active" : ""].join(' ')} /> )}
        </div>
    )
}

export const Separator = (
    props
) => {
    return (
        <div className="step__line-container">
            <div className="step__line">
                {Array(5).fill(0).map((_, i) => <div key={i} className="dot" /> )}
            </div>
        </div>
    )
}

export const ProcessStep = (
    /** @type {{ step: ProcessStep, index: number, observer: IntersectionObserver, isLast: boolean, isMobile: boolean, showSeparator: boolean }} */
    props
) => {
    const ProcessStepPart = ({ source }) => {
        const parsed = React.useMemo(() => MarkdownHelper.parse(source || ''), [source])

        return <ReactMarkdown
            {...MarkdownHelper.allTypes()}
            source={parsed || source}
            escapeHtml={false}
        />
    }

    const ref = React.useRef(null)

    React.useEffect(() => {
        if (props.isMobile && props.observer) {
            if (!props.observer) {
                return
            }
            props.observer.observe(ref.current)
        }

        return () => {
            if (!props.observer) {
                return
            }
            props.observer.unobserve(ref.current)
        }
    }, [props.isMobile, props.observer])

    const showSeparator = props.showSeparator && !props.isLast
    const separatorPosition = props.isMobile ? 'below' : 'left'

    return (
        <React.Fragment>
            <div className="step" data-index={props.index} ref={ref}>
                <div className="step__left">
                    <div className="step__icon">
                        <img src={props.step.icon} />
                    </div>
                    {showSeparator && separatorPosition === 'left' && (
                        <Separator />
                    )}
                </div>
    
                <div className="step__content">
                    <div className="step__headline">
                        <ReactMarkdown
                            source={MarkdownHelper.parse(props.step.headline)}
                            {...MarkdownHelper.basicTypes()}
                            escapeHtml={false}
                        />
                    </div>
                    <div className="step__body">
                        <ReactMarkdown
                            source={MarkdownHelper.parse(props.step.body)}
                            {...MarkdownHelper.basicTypes()}
                            escapeHtml={false}
                        />
                    </div>
    
                    {props.step.ctaLink && props.step.ctaType === 'link' && (
                        <a className="step__cta-link">{props.step.ctaLink}</a>
                    )}
                    {props.step.ctaLink && props.step.ctaType === 'button' && (
                        <a className="step__cta-button">{props.step.ctaLink}</a>
                    )}
                </div>
                {showSeparator && separatorPosition === 'below' && (
                    <Separator />
                )}
            </div>
        </React.Fragment>
    )
}

/**
 * @param {Record<string, string>} campaign
 * @returns {ProcessStep[]}
 */
export const transformProcessSteps = (campaign) => {
    const range = [...Array(4).keys()]
    return range.map((ri) => {
        const i = ri + 1
        const icon = campaign[`process_container_step_${i}_icon`]

        let headline = campaign[`process_container_step_${i}_headline`]
        let body = campaign[`process_container_step_${i}_copy_text`]

        const ctaLink = campaign[`process_container_step_${i}_ctalink`]
        const ctaType = campaign['process_container_1_4_cta_link_click'] === 'CTA' ? 'button' : 'link'

        // if any of these are missing, we probably don't want to render the step
        if (![icon, body].every(Boolean)) {
            return null
        }

        return {
            icon,
            headline,
            body,
            ctaLink,
            ctaType,
        }
    })
    // reject falsy values
    .filter(Boolean)
}



// WEBPACK FOOTER //
// ./src/components/ads/mam3/ProcessStep.js